import type { DocumentData } from "firebase/firestore";

export const AI_USAGE_PERIODS = ["hour", "day", "week", "month", "year"] as const;
export const MAX_AI_USAGE_TOTAL_TOKENS = 100_000_000_000;

export type AIUsagePeriod = (typeof AI_USAGE_PERIODS)[number];
export type AIUsageSummaryMode = "league" | "personal";

export interface AIUsageLimit {
  enabled: boolean;
  totalTokens: number;
  period: AIUsagePeriod;
}

export const AI_USAGE_UNLIMITED_PLACEHOLDER: AIUsageLimit = {
  enabled: false,
  totalTokens: 1_000_000,
  period: "month",
};

export interface AIUsagePerUserEntry {
  userID: string;
  displayName: string;
  photoURL?: string;
  totalTokens: number;
}

export interface AIUsageSummary {
  mode: AIUsageSummaryMode;
  leagueID?: string;
  tier: "free" | "paid" | "internal";
  limit: AIUsageLimit | null;
  period: AIUsagePeriod | null;
  periodStart: Date;
  nextResetAt: Date;
  usedTokens: number;
  remainingTokens: number | null;
  isOverLimit: boolean;
  scannedEventCount: number;
  truncated: boolean;
  perUser?: AIUsagePerUserEntry[];
}

export interface AIUsagePeriodBounds {
  periodStart: Date;
  nextResetAt: Date;
}

export class AIUsageDecodeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AIUsageDecodeError";
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isPeriod(value: unknown): value is AIUsagePeriod {
  return typeof value === "string" && AI_USAGE_PERIODS.includes(value as AIUsagePeriod);
}

function isTier(value: unknown): value is AIUsageSummary["tier"] {
  return value === "free" || value === "paid" || value === "internal";
}

function nonNegativeInteger(value: unknown, field: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
    throw new AIUsageDecodeError(`${field} must be a non-negative integer`);
  }
  return value;
}

function requiredBoolean(value: unknown, field: string): boolean {
  if (typeof value !== "boolean") throw new AIUsageDecodeError(`${field} must be a boolean`);
  return value;
}

function requiredISODate(value: unknown, field: string): Date {
  if (typeof value !== "string" || !value.trim()) {
    throw new AIUsageDecodeError(`${field} must be an ISO-8601 string`);
  }
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) throw new AIUsageDecodeError(`${field} is invalid`);
  return date;
}

/** Defensive parser shared by Firestore limits and HTTP summaries. */
export function parseAIUsageLimit(value: unknown): AIUsageLimit | null {
  if (!isRecord(value) || typeof value.enabled !== "boolean") return null;
  if (
    typeof value.totalTokens !== "number"
    || !Number.isSafeInteger(value.totalTokens)
    || value.totalTokens <= 0
    || value.totalTokens > MAX_AI_USAGE_TOTAL_TOKENS
    || !isPeriod(value.period)
  ) {
    return null;
  }
  return { enabled: value.enabled, totalTokens: value.totalTokens, period: value.period };
}

export function aiUsageLimitFirestoreData(limit: AIUsageLimit): Record<string, unknown> {
  const parsed = parseAIUsageLimit(limit);
  if (!parsed) throw new AIUsageDecodeError("AI usage limit is invalid");
  return { ...parsed };
}

export function parseFreeTierAIUsageLimit(data: DocumentData | undefined): AIUsageLimit | null {
  return parseAIUsageLimit(data?.freeTier);
}

function decodePerUser(value: unknown): AIUsagePerUserEntry[] | undefined {
  if (value === undefined) return undefined;
  if (!Array.isArray(value)) throw new AIUsageDecodeError("perUser must be an array");
  return value.map((entry, index) => {
    if (!isRecord(entry) || typeof entry.userID !== "string" || !entry.userID) {
      throw new AIUsageDecodeError(`perUser[${index}].userID is required`);
    }
    if (typeof entry.displayName !== "string" || !entry.displayName) {
      throw new AIUsageDecodeError(`perUser[${index}].displayName is required`);
    }
    if (entry.photoURL !== undefined && typeof entry.photoURL !== "string") {
      throw new AIUsageDecodeError(`perUser[${index}].photoURL must be a string`);
    }
    return {
      userID: entry.userID,
      displayName: entry.displayName,
      ...(entry.photoURL ? { photoURL: entry.photoURL } : {}),
      totalTokens: nonNegativeInteger(entry.totalTokens, `perUser[${index}].totalTokens`),
    };
  });
}

/** Strict decoder for POST /ai-usage/summary. */
export function decodeAIUsageSummary(value: unknown): AIUsageSummary {
  if (!isRecord(value)) throw new AIUsageDecodeError("AI usage summary must be an object");
  if (value.mode !== "league" && value.mode !== "personal") {
    throw new AIUsageDecodeError("mode must be league or personal");
  }
  if (!isTier(value.tier)) throw new AIUsageDecodeError("tier is invalid");
  if (value.leagueID !== undefined && (typeof value.leagueID !== "string" || !value.leagueID)) {
    throw new AIUsageDecodeError("leagueID must be a non-empty string");
  }
  if (value.mode === "league" && typeof value.leagueID !== "string") {
    throw new AIUsageDecodeError("league summaries require leagueID");
  }
  if (value.mode === "personal" && value.leagueID !== undefined) {
    throw new AIUsageDecodeError("personal summaries cannot include leagueID");
  }

  let limit: AIUsageLimit | null;
  if (value.limit === null) limit = null;
  else {
    limit = parseAIUsageLimit(value.limit);
    if (!limit) throw new AIUsageDecodeError("limit is invalid");
  }

  let period: AIUsagePeriod | null;
  if (value.period === null) period = null;
  else if (isPeriod(value.period)) period = value.period;
  else throw new AIUsageDecodeError("period is invalid");

  if (limit && period !== limit.period) {
    throw new AIUsageDecodeError("period must match limit.period");
  }
  if (!limit && period !== null) throw new AIUsageDecodeError("unlimited summaries must use a null period");

  const remainingTokens = value.remainingTokens === null
    ? null
    : nonNegativeInteger(value.remainingTokens, "remainingTokens");
  const perUser = decodePerUser(value.perUser);
  return {
    mode: value.mode,
    ...(typeof value.leagueID === "string" ? { leagueID: value.leagueID } : {}),
    tier: value.tier,
    limit,
    period,
    periodStart: requiredISODate(value.periodStart, "periodStart"),
    nextResetAt: requiredISODate(value.nextResetAt, "nextResetAt"),
    usedTokens: nonNegativeInteger(value.usedTokens, "usedTokens"),
    remainingTokens,
    isOverLimit: requiredBoolean(value.isOverLimit, "isOverLimit"),
    scannedEventCount: nonNegativeInteger(value.scannedEventCount, "scannedEventCount"),
    truncated: requiredBoolean(value.truncated, "truncated"),
    ...(perUser ? { perUser } : {}),
  };
}

/** UTC calendar-aligned bounds matching functions/src/aiUsageLimits.ts. */
export function aiUsagePeriodBounds(period: AIUsagePeriod, now: Date): AIUsagePeriodBounds {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const day = now.getUTCDate();
  switch (period) {
    case "hour": {
      const start = Date.UTC(year, month, day, now.getUTCHours());
      return { periodStart: new Date(start), nextResetAt: new Date(start + 3_600_000) };
    }
    case "day": {
      const start = Date.UTC(year, month, day);
      return { periodStart: new Date(start), nextResetAt: new Date(start + 86_400_000) };
    }
    case "week": {
      const daysSinceMonday = (now.getUTCDay() + 6) % 7;
      const start = Date.UTC(year, month, day - daysSinceMonday);
      return { periodStart: new Date(start), nextResetAt: new Date(start + 7 * 86_400_000) };
    }
    case "month":
      return {
        periodStart: new Date(Date.UTC(year, month, 1)),
        nextResetAt: new Date(Date.UTC(year, month + 1, 1)),
      };
    case "year":
      return {
        periodStart: new Date(Date.UTC(year, 0, 1)),
        nextResetAt: new Date(Date.UTC(year + 1, 0, 1)),
      };
  }
}

export function aiUsageRemainingPercent(summary: AIUsageSummary): number | null {
  if (!summary.limit?.enabled || summary.limit.totalTokens <= 0) return null;
  const remaining = Math.max(0, summary.limit.totalTokens - summary.usedTokens);
  return Math.max(0, Math.min(100, Math.round((remaining / summary.limit.totalTokens) * 100)));
}

export function aiUsageResetDescription(nextResetAt: Date, now = new Date()): string {
  const seconds = (nextResetAt.getTime() - now.getTime()) / 1_000;
  if (seconds <= 0) return "soon";
  if (seconds < 3_600) {
    const minutes = Math.max(1, Math.floor(seconds / 60));
    return `in ${minutes} minute${minutes === 1 ? "" : "s"}`;
  }
  if (seconds < 86_400) {
    const hours = Math.max(1, Math.floor(seconds / 3_600));
    return `in ${hours} hour${hours === 1 ? "" : "s"}`;
  }
  const days = Math.max(1, Math.floor(seconds / 86_400));
  return `in ${days} day${days === 1 ? "" : "s"}`;
}
